/**
 * ActionCard -- renders inline action cards in chat for tool completions.
 *
 * Supports: create_task, create_event, set_reminder, update_task, complete_task.
 * Parses the tool result JSON and renders a styled card with relevant fields.
 */

interface ActionCardProps {
  toolName: string;
  result: string;
}

/**
 * Priority badge color mapping using design system tokens
 */
function priorityClasses(priority: string): string {
  switch (priority) {
    case "critical":
      return "text-priority-critical bg-priority-critical/10";
    case "high":
      return "text-priority-high bg-priority-high/10";
    case "medium":
      return "text-priority-medium bg-priority-medium/10";
    case "low":
      return "text-priority-low bg-priority-low/10";
    default:
      return "text-text-tertiary bg-surface-active";
  }
}

/**
 * Formats an ISO date string for display
 */
function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString();
  } catch {
    return dateStr;
  }
}

/**
 * Formats an ISO datetime string for display
 */
function formatDateTime(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleString([], {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return dateStr;
  }
}

/**
 * Safely extract a string field from parsed JSON data
 */
function str(data: Record<string, unknown>, key: string): string | undefined {
  const val = data[key];
  return typeof val === "string" ? val : undefined;
}

export function ActionCard({ toolName, result }: ActionCardProps): React.ReactElement | null {
  let data: Record<string, unknown>;
  try {
    data = JSON.parse(result) as Record<string, unknown>;
  } catch {
    return null;
  }

  const title = str(data, "title");
  const priority = str(data, "priority");
  const dueDate = str(data, "dueDate");
  const eventType = str(data, "type");
  const startTime = str(data, "startTime");
  const remindAt = str(data, "remindAt");

  if (toolName === "create_task") {
    return (
      <div className="my-2 rounded-lg border border-border bg-surface-hover p-3">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-mono text-text-tertiary">
            TASK CREATED
          </span>
          {priority && (
            <span
              className={`text-xs font-mono px-1.5 py-0.5 rounded ${priorityClasses(priority)}`}
            >
              {priority}
            </span>
          )}
        </div>
        <p className="text-sm font-medium text-text-primary">
          {title}
        </p>
        {dueDate && (
          <p className="text-xs font-mono text-text-tertiary mt-1">
            Due: {formatDate(dueDate)}
          </p>
        )}
      </div>
    );
  }

  if (toolName === "create_event") {
    return (
      <div className="my-2 rounded-lg border border-border bg-surface-hover p-3">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-mono text-text-tertiary">
            EVENT CREATED
          </span>
          {eventType && (
            <span className="text-xs font-mono px-1.5 py-0.5 rounded text-primary bg-primary-subtle">
              {eventType}
            </span>
          )}
        </div>
        <p className="text-sm font-medium text-text-primary">
          {title}
        </p>
        {startTime && (
          <p className="text-xs font-mono text-text-tertiary mt-1">
            {formatDateTime(startTime)}
          </p>
        )}
      </div>
    );
  }

  if (toolName === "set_reminder") {
    return (
      <div className="my-2 rounded-lg border border-border bg-surface-hover p-3">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-mono text-text-tertiary">
            REMINDER SET
          </span>
        </div>
        <p className="text-sm font-medium text-text-primary">
          {title}
        </p>
        {remindAt && (
          <p className="text-xs font-mono text-text-tertiary mt-1">
            {formatDateTime(remindAt)}
          </p>
        )}
      </div>
    );
  }

  if (toolName === "update_task") {
    return (
      <div className="my-2 rounded-lg border border-border bg-surface-hover p-3">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-mono text-text-tertiary">
            TASK UPDATED
          </span>
        </div>
        <p className="text-sm font-medium text-text-primary">
          {title}
        </p>
      </div>
    );
  }

  if (toolName === "complete_task") {
    return (
      <div className="my-2 rounded-lg border border-border bg-surface-hover p-3">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-mono text-text-tertiary">
            TASK COMPLETED
          </span>
        </div>
        <p className="text-sm font-medium text-text-primary line-through">
          {title}
        </p>
      </div>
    );
  }

  // Unrecognized tool -- don't render
  return null;
}
